import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  MenuItem, Checkbox, Radio, RadioGroup,
  FormControlLabel, FormLabel, CircularProgress, Tooltip, Switch
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { generateEstimatePDF, printEstimatePDF, shareEstimateWhatsApp } from '../../../utils/estimatePdfGenerator';

// ─── Constants ───────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 1, label: 'New' },
  { value: 2, label: 'InProgress' },
  { value: 3, label: 'Pending' },
  { value: 4, label: 'Completed' },
  { value: 5, label: 'Delivered' },
  { value: 6, label: 'Cancelled' },
];

const PAYMENT_OPTIONS = [
  { value: 1, label: 'Cash' },
  { value: 2, label: 'Card' },
  { value: 3, label: 'UPI' },
];

const today = () => new Date().toISOString().split('T')[0];

const EMPTY_HEADER = {
  OrderNumber: '', OrderDate: today(), EstimateDate: today(),
  ClientId: null, StatusId: 1,
  ClientName: '', ContactNumber: '', Address: '', Remarks: '',
  Amount: 0, OtherAmount: 0, TotalAmount: 0,
  AdvanceAmount: 0, PaidAmount: 0, CardAmount: 0, UpiAmount: 0,
  BalanceAmount: 0, Roundoff: 0,
  DiscountRate: 0, AdditionalDiscRate: 0, Location: '', PaymentType: 1, PaymentBankId: null,
  EstimateFlag: false, FinYear: '',
  IsPettyCash: false,
};

const EMPTY_DETAIL = {
  Width: '', Height: '', MountWidth: '',
  SizeId: null, FrameId: null, Thicknes: '', Length: '',
  GlassId: null, MountId: null, BoardId: null,
  LaminationId: null, TapeId: null, HookId: null, HookCount: 0,
  StandId: null, PrintId: null,
  Quantity: 1, DiscAmt: 0, Discper: 0, AdditionalDiscAmt: 0, AdditionalDiscPer: 0, Amount: 0, Rate: 0,
  Perimeter: 0, Area: 0,
  // Rates (auto-filled from lookup masters)
  FrameRate: 0, GlassRate: 0, MountRate: 0, BoardRate: 0,
  LaminationRate: 0, TapeRate: 0, StandRate: 0, StandLabourCharge: 0,
  PrintRate: 0,
};

// ─── Calculation Engine (mirrors C# backend logic) ────────────────────────────
function calcRowAmount(row, sizesList, printsList, hooksList, companySettings) {
  const {
    TapeWastage = 0, MountWastage = 0, FrameWastage = 0,
    GLassWastage = 0, BoardWastage = 0, LaminationWastage = 0,
    TotalMargin: TotalMarginper = 0,
  } = companySettings || {};

  const w       = parseFloat(row.Width)      || 0;
  const h       = parseFloat(row.Height)     || 0;
  const mw      = parseFloat(row.MountWidth) || 0;
  const thick   = parseFloat(row.Thicknes)   || 0;
  const qty     = parseFloat(row.Quantity)   || 1;
  const discAmt    = parseFloat(row.DiscAmt)           || 0;
  const discPer    = parseFloat(row.Discper)            || 0;
  const addDiscAmt = parseFloat(row.AdditionalDiscAmt)  || 0;
  const addDiscPer = parseFloat(row.AdditionalDiscPer)  || 0;

  // Perimeter (matches C# exactly)
  const wCalc     = Math.round(w     * 2 * 100) / 100;
  const hCalc     = Math.round(h     * 2 * 100) / 100;
  const thCalc    = Math.round(thick * 8 * 100) / 100;
  const mwCalc    = Math.round(mw    * 8 * 100) / 100;
  const perimeter = Math.round((wCalc + hCalc + mwCalc + thCalc) * 100) / 100;

  // Area (with mount border)
  const area = (w + mw * 2) * (h + mw * 2);

  // Print area
  let printarea = w * h;
  if (row.PrintId) {
    const printer = printsList.find(p => p.Id === row.PrintId);
    if (printer?.RollWidth) printarea = printer.RollWidth * h;
  }

  // Material costs
  const GlassRate      = Math.round((row.GlassRate      || 0) * area      * 100) / 100;
  const PrintRate      = Math.round((row.PrintRate      || 0) * printarea  * 100) / 100;
  const LaminationRate = Math.round((row.LaminationRate || 0) * area      * 100) / 100;
  const BoardRate      = Math.round((row.BoardRate      || 0) * area      * 100) / 100;
  const MountRate      = Math.round((row.MountRate      || 0) * area      * 100) / 100;
  const TapeRate       = Math.round((row.TapeRate       || 0) * perimeter * 100) / 100;
  const FrameRate      = Math.round((row.FrameRate      || 0) * perimeter * 100) / 100;
  const StandRate      = parseFloat(row.StandRate)        || 0;
  const StandLabour    = parseFloat(row.StandLabourCharge) || 0;

  // Wastage margins
  const framemargin      = FrameWastage      ? Math.round(FrameRate      * FrameWastage      / 100 * 100) / 100 : 0;
  const mountmargin      = MountWastage      ? Math.round(MountRate      * MountWastage      / 100 * 100) / 100 : 0;
  const tapemargin       = TapeWastage       ? Math.round(TapeRate       * TapeWastage       / 100 * 100) / 100 : 0;
  const glassmargin      = GLassWastage      ? Math.round(GlassRate      * GLassWastage      / 100 * 100) / 100 : 0;
  const boardmargin      = BoardWastage      ? Math.round(BoardRate      * BoardWastage      / 100 * 100) / 100 : 0;
  const laminationmargin = LaminationWastage ? Math.round(LaminationRate * LaminationWastage / 100 * 100) / 100 : 0;

  // Size-based labour (lookup by area range, fallback to row's existing SizeId)
  let sizeDetail = sizesList.find(s => s.AreaFrom <= area && s.AreaTo >= area) || null;
  if (!sizeDetail && row.SizeId) {
    sizeDetail = sizesList.find(s => s.Id === row.SizeId) || null;
  }
  const sizerate   = sizeDetail?.Rate || 0;
  const sizeamt    = sizerate ? Math.round(sizerate * area * 100) / 100 : 0;

  const LabourBoard      = (row.BoardRate      || 0) === 0 ? 0 : (sizeDetail?.LabourBoard      || 0);
  const LabourFrame      = (row.FrameRate      || 0) === 0 ? 0 : (sizeDetail?.LabourFrame      || 0);
  const LabourGlass      = (row.GlassRate      || 0) === 0 ? 0 : (sizeDetail?.LabourGlass      || 0);
  const LabourMount      = (row.MountRate      || 0) === 0 ? 0 : (sizeDetail?.LabourMount      || 0);
  const LabourLamination = (row.LaminationRate || 0) === 0 ? 0 : (sizeDetail?.LabourLamination || 0);

  // Hook rate — auto-matched from Size name (mirrors C# hookDetails lookup)
  let HookRate = 0;
  const hookCount = parseInt(row.HookCount) || 0;
  if (hookCount > 0 && sizeDetail?.HookRate) {
    HookRate = Math.round(hookCount * sizeDetail.HookRate * 100) / 100;
  }
  // Auto-resolve HookId from hooks list by matching Size name
  const hookDetail = hooksList.find(h => h.Name === sizeDetail?.Name) || null;
  const resolvedHookId = hookDetail?.Id || row.HookId || null;

  // Sub-total before print & total margin
  let totamt =
    framemargin + mountmargin + glassmargin + boardmargin + laminationmargin +
    LabourBoard + tapemargin + LabourFrame + LabourGlass + LabourMount + LabourLamination +
    FrameRate + StandRate + StandLabour + HookRate +
    TapeRate + LaminationRate + BoardRate + MountRate + GlassRate ;

  // Total margin %
  const totalmargin = TotalMarginper ? Math.round(totamt * TotalMarginper / 100 * 100) / 100 : 0;
  totamt = Math.round((totamt + PrintRate + totalmargin) * 100) / 100;

  // Row-level discount: two modes
  // 1) If discPer is set → compute DiscAmt from it
  // 2) If discAmt is set (and discPer == 0) → back-calculate Discper from DiscAmt
  let computedDiscAmt = 0;
  let computedDiscPer = discPer;
  if (discPer !== 0) {
    computedDiscAmt = Math.round(totamt * discPer / 100 * 100) / 100;
  } else if (discAmt !== 0) {
    computedDiscAmt = discAmt;
    computedDiscPer = totamt !== 0 ? Math.round(discAmt / totamt * 100 * 100) / 100 : 0;
  }

  // Additional discount — two modes, exactly 2 decimal places
  // 1) If addDiscPer is set → compute AdditionalDiscAmt from it
  // 2) If addDiscAmt is set (and addDiscPer == 0) → back-calculate AdditionalDiscPer
  const afterMainDisc = totamt - computedDiscAmt;
  let computedAddDiscAmt = 0;
  let computedAddDiscPer = addDiscPer;
  if (addDiscPer !== 0) {
    computedAddDiscAmt = parseFloat((afterMainDisc * addDiscPer / 100).toFixed(2));
  } else if (addDiscAmt !== 0) {
    computedAddDiscAmt = parseFloat(addDiscAmt.toFixed(2));
    computedAddDiscPer = afterMainDisc !== 0
      ? parseFloat((addDiscAmt / afterMainDisc * 100).toFixed(2))
      : 0;
  }

  // Rate = unit cost after both discounts
  const rate = Math.round((afterMainDisc - computedAddDiscAmt) * 100) / 100;

  // Amount = Rate * Qty
  const finalAmount = Math.round(rate * qty * 100) / 100;

  return {
    perimeter,
    area:              Math.round(area * 10000) / 10000,
    Rate:              rate,
    Amount:            finalAmount,
    DiscAmt:           computedDiscAmt,
    Discper:           computedDiscPer,
    AdditionalDiscAmt: computedAddDiscAmt,
    AdditionalDiscPer: computedAddDiscPer,
    SizeId:   sizeDetail?.Id   || row.SizeId || null,
    SizeName: sizeDetail?.Name || '',
    SizeRate: sizerate,
    HookId:   resolvedHookId,
    // Length = perimeter in inches (width + height) * 2, matching frame cutting length
    Length:   perimeter,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
const EstimateModal = ({ open, onClose, onSave, EstimateData = null, mode = 'create', companyData = {}, onConvertToBill }) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Header form
  const [formData, setFormData] = useState(EMPTY_HEADER);
  const [errors, setErrors] = useState({});

  // Detail rows
  const [details, setDetails] = useState([]);
  const [editingRow, setEditingRow] = useState(null); // index of row being edited

  // Lookup data
  const [masters, setMasters] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [frames, setFrames] = useState([]);
  const [glasses, setGlasses] = useState([]);
  const [mounts, setMounts] = useState([]);
  const [boards, setBoards] = useState([]);
  const [laminations, setLaminations] = useState([]);
  const [tapes, setTapes] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [stands, setStands] = useState([]);
  const [prints, setPrints] = useState([]);
  const [banks, setBanks] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [companySettings, setCompanySettings] = useState({});

  // Quick-create client
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [createClientData, setCreateClientData] = useState({ Name: '', ContactNumber: '', AddressLine1: '', AddressLine2: '', GSTNumber: '', Remarks: '', OpeningBalance: '', IsPayable: false, PostCode: '', PostalId: 0, PostHeadOffice: '', PostPlace: '', District: '', State: '', Country: '' });
  const [createClientLoading, setCreateClientLoading] = useState(false);
  const [createClientErrors, setCreateClientErrors] = useState({});
  const [createClientDupOpen, setCreateClientDupOpen] = useState(false);
  const [createClientDupMsg, setCreateClientDupMsg] = useState('');
  // Postal state for quick-create dialog
  const [ccAllPostal, setCcAllPostal] = useState([]);
  const [ccPostalOptions, setCcPostalOptions] = useState([]);
  const [ccPostalInput, setCcPostalInput] = useState('');
  const [ccPostalLoading, setCcPostalLoading] = useState(false);
  const [ccSelectedPostal, setCcSelectedPostal] = useState(null);

  // Ref for auto-focusing ContactNumber when number is typed
  const contactNumberRef = useRef(null);

  // Responsive
  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load lookups once
  useEffect(() => {
    if (!open || lookupsLoaded || !companyId || !token) return;
    const load = async (ctrl, setter) => {
      try {
        const r = await fetchData(ctrl, `/${companyId}`, token);
        if (r?.responseCode === '1111' && r.response_data) setter(Array.isArray(r.response_data) ? r.response_data : [r.response_data]);
      } catch (_) {}
    };
    Promise.all([
      load('MasterApi', setMasters),
      load('SizeApi', setSizes),
      load('FrameApi', setFrames),
      load('GlassApi', setGlasses),
      load('MountApi', setMounts),
      load('BoardApi', setBoards),
      load('LaminationApi', setLaminations),
      load('TapeApi', setTapes),
      load('HookApi', setHooks),
      load('StandApi', setStands),
      load('PrintApi', setPrints),
      load('BankApi', setBanks),
      // Company wastage/margin settings
      fetchData('CompanyApi', `/Details/${companyId}`, token)
        .then(r => { if (r?.responseCode === '1111' && r.response_data) setCompanySettings(r.response_data); })
        .catch(() => {}),
    ]).then(() => setLookupsLoaded(true));
  }, [open, lookupsLoaded, companyId, token]);

  // Populate form on open
  useEffect(() => {
    if (!open) return;
    if (EstimateData && mode !== 'create') {
      // Fetch full record with details from API
      setLoading(true);
      fetchData('EstimateApi', `/${EstimateData.Id}`, token)
        .then(r => {
          const d = (r?.responseCode === '1111' && r.response_data) ? r.response_data : EstimateData;
          setFormData({
            Id:             d.Estimate.Id             ?? EstimateData.Id ?? null,
            IsConverted:    d.Estimate.IsConverted    ?? false,
            OrderNumber:    d.Estimate.OrderNumber    ?? '',
            OrderDate:      d.Estimate.OrderDate      ? d.Estimate.OrderDate.split('T')[0]      : today(),
            EstimateDate:   d.Estimate.EstimateDate   ? d.Estimate.EstimateDate.split('T')[0]   : today(),
            ClientId:       d.Estimate.ClientId       ?? null,
            StatusId:       parseInt(d.Estimate.StatusId)  || 1,
            ClientName:     d.Estimate.ClientName     ?? '',
            ContactNumber:  d.Estimate.ContactNumber  ?? '',
            Address:        d.Estimate.Address        ?? '',
            Remarks:        d.Estimate.Remarks        ?? '',
            Amount:         d.Estimate.Amount         ?? 0,
            OtherAmount:    d.Estimate.OtherAmount    ?? 0,
            TotalAmount:    d.Estimate.TotalAmount    ?? 0,
            AdvanceAmount:  d.Estimate.AdvanceAmount  ?? 0,
            PaidAmount:     d.Estimate.PaidAmount     ?? 0,
            CardAmount:     d.Estimate.CardAmount     ?? 0,
            UpiAmount:      d.Estimate.UpiAmount      ?? 0,
            BalanceAmount:  d.Estimate.BalanceAmount  ?? 0,
            Roundoff:       d.Estimate.Roundoff       ?? 0,
            DiscountRate:      d.Estimate.DiscountRate      ?? 0,
            AdditionalDiscRate: d.Estimate.AdditionalDiscPer ?? 0,
            Location:       parseInt(d.Estimate.Location) || '',
            PaymentType:    parseInt(d.Estimate.PaymentType) || 1,
            PaymentBankId:  d.Estimate.PaymentBankId ?? null,
            EstimateFlag:   d.Estimate.EstimateFlag   ?? false,
            IsPettyCash:    d.Estimate.IsPettyCash    ?? false,
            FinYear:        d.Estimate.FinYear        ?? '',
          });
          setDetails(d.EstimateDetails ?? []);
        })
        .catch(() => {
          // Fallback to passed data if API fails
          setFormData({
            Id:             EstimateData.Id           ?? null,
            IsConverted:    EstimateData.IsConverted  ?? false,
            OrderNumber:    EstimateData.OrderNumber    ?? '',
            OrderDate:      EstimateData.OrderDate      ? EstimateData.OrderDate.split('T')[0]      : today(),
            EstimateDate:   EstimateData.EstimateDate   ? EstimateData.EstimateDate.split('T')[0]   : today(),
            ClientId:       EstimateData.ClientId       ?? null,
            StatusId:       parseInt(EstimateData.StatusId)  || 1,
            ClientName:     EstimateData.ClientName     ?? '',
            ContactNumber:  EstimateData.ContactNumber  ?? '',
            Address:        EstimateData.Address        ?? '',
            Remarks:        EstimateData.Remarks        ?? '',
            Amount:         EstimateData.Amount         ?? 0,
            OtherAmount:    EstimateData.OtherAmount    ?? 0,
            TotalAmount:    EstimateData.TotalAmount    ?? 0,
            AdvanceAmount:  EstimateData.AdvanceAmount  ?? 0,
            PaidAmount:     EstimateData.PaidAmount     ?? 0,
            CardAmount:     EstimateData.CardAmount     ?? 0,
            UpiAmount:      EstimateData.UpiAmount      ?? 0,
            BalanceAmount:  EstimateData.BalanceAmount  ?? 0,
            Roundoff:       EstimateData.Roundoff       ?? 0,
            DiscountRate:      EstimateData.DiscountRate      ?? 0,
            AdditionalDiscRate: EstimateData.AdditionalDiscPer ?? 0,
            Location:       parseInt(EstimateData.Location) || '',
            PaymentType:    parseInt(EstimateData.PaymentType) || 1,
            PaymentBankId:  EstimateData.PaymentBankId ?? null,
            EstimateFlag:   EstimateData.EstimateFlag   ?? false,
            IsPettyCash:    EstimateData.IsPettyCash    ?? false,
            FinYear:        EstimateData.FinYear        ?? '',
          });
          setDetails(EstimateData.EstimateDetails ?? []);
        })
        .finally(() => setLoading(false));
    } else {
      setFormData(EMPTY_HEADER);
      setDetails([]);
      // Fetch max OrderNumber from API for new estimates
      if (companyId && token) {
        fetchData('EstimateApi', `/MaxBillno/${companyId}`, token)
          .then(r => {
            const maxNo = r?.response_data.OrderNumber ?? r?.responseData ?? r;
            const next = (parseInt(maxNo) || 0) ;
            setFormData(prev => ({ ...prev, OrderNumber: next }));
          })
          .catch(() => {});
      }
    }
    setErrors({});
    setEditingRow(null);
  }, [open, EstimateData, mode]);

  // ─── Recalculate totals whenever details or other amounts change ───────────
  useEffect(() => {
    const subTotal = details.reduce((s, d) => s + (parseFloat(d.Amount) || 0), 0);
    const other = parseFloat(formData.OtherAmount) || 0;
    const raw = subTotal + other;
    const roundoff = Math.round(raw) - raw;
    const total = raw + roundoff;
    const advance = parseFloat(formData.AdvanceAmount) || 0;
    const paid = parseFloat(formData.PaidAmount) || 0;
    const card = parseFloat(formData.CardAmount) || 0;
    const upi = parseFloat(formData.UpiAmount) || 0;
    const balance = total - advance - paid - card - upi;
    setFormData(prev => ({
      ...prev,
      Amount: parseFloat(subTotal.toFixed(2)),
      Roundoff: parseFloat(roundoff.toFixed(2)),
      TotalAmount: parseFloat(total.toFixed(2)),
      BalanceAmount: parseFloat(balance.toFixed(2)),
    }));
  }, [details, formData.OtherAmount, formData.AdvanceAmount, formData.PaidAmount, formData.CardAmount, formData.UpiAmount]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const paymentFields = ['AdvanceAmount', 'PaidAmount', 'CardAmount', 'UpiAmount'];
    if (paymentFields.includes(name)) {
      const newVal = parseFloat(value) || 0;
      if (newVal < 0) return; // block negative input
      setFormData(prev => {
        const updated = { ...prev, [name]: value };
        const advance = parseFloat(name === 'AdvanceAmount' ? value : prev.AdvanceAmount) || 0;
        const paid    = parseFloat(name === 'PaidAmount'    ? value : prev.PaidAmount)    || 0;
        const card    = parseFloat(name === 'CardAmount'    ? value : prev.CardAmount)    || 0;
        const upi     = parseFloat(name === 'UpiAmount'     ? value : prev.UpiAmount)     || 0;
        const total   = parseFloat(prev.TotalAmount) || 0;
        const balance = parseFloat((total - advance - paid - card - upi).toFixed(2));
        if (balance < 0) return prev; // block: would cause negative balance
        return { ...updated, BalanceAmount: balance };
      });
      if (errors.payment) setErrors(prev => ({ ...prev, payment: '' }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleClientSelect = (_, selected) => {
    if (selected && selected.__createNew) {
      // User clicked "+ Create Client"
      const inputVal = selected.__inputValue || '';
      const isNumber = /^\d+$/.test(inputVal);
      setCreateClientData({
        Name: isNumber ? '' : inputVal,
        ContactNumber: isNumber ? inputVal : '',
        AddressLine1: '', AddressLine2: '', GSTNumber: '', Remarks: '', OpeningBalance: '', IsPayable: false, PostCode: '', PostalId: 0, PostHeadOffice: '', PostPlace: '', District: '', State: '', Country: ''
      });
      setCreateClientErrors({});
      setCcSelectedPostal(null);
      setCcPostalInput('');
      setCcPostalOptions([]);
      // Load postal data if not yet loaded
      if (ccAllPostal.length === 0) {
        setCcPostalLoading(true);
        fetchData('MasterApi', `/pincode/${companyId}`, token)
          .then(r => {
            if (r?.responseCode === '1111' && r.response_data) {
              const data = Array.isArray(r.response_data) ? r.response_data : [r.response_data];
              setCcAllPostal(data);
              setCcPostalOptions(data.slice(0, 50));
            }
          })
          .catch(() => {})
          .finally(() => setCcPostalLoading(false));
      } else {
        setCcPostalOptions(ccAllPostal.slice(0, 50));
      }
      setCreateClientOpen(true);
      // Auto-focus ContactNumber field if a number was typed
      if (isNumber) {
        setTimeout(() => {
          contactNumberRef.current?.focus();
        }, 100);
      }
      return;
    }
    if (selected) {
      setFormData(prev => ({
        ...prev,
        ClientId: selected.Id,
        ClientName: selected.Name || '',
        ContactNumber: selected.ContactNumber || '',
        Address: selected.AddressLine1 || '',
      }));
    } else {
      setFormData(prev => ({ ...prev, ClientId: null, ClientName: '', ContactNumber: '', Address: '' }));
    }
  };

  // Postal handlers for quick-create dialog
  const handleCcPostalInputChange = (_, val) => {
    setCcPostalInput(val);
    if (!val || val.length < 1) { setCcPostalOptions(ccAllPostal.slice(0, 50)); return; }
    const q = val.toLowerCase();
    setCcPostalOptions(ccAllPostal.filter(p =>
      p.PinCode?.toLowerCase().includes(q) ||
      p.PostPlace?.toLowerCase().includes(q) ||
      p.District?.toLowerCase().includes(q)
    ).slice(0, 50));
  };

  const handleCcPostalSelect = (_, selected) => {
    setCcSelectedPostal(selected);
    if (selected) {
      setCreateClientData(prev => ({
        ...prev,
        PostCode: selected.PinCode || '',
        PostalId: selected.Id || 0,
        PostHeadOffice: selected.PostHeadOffice || '',
        PostPlace: selected.PostPlace || '',
        District: selected.District || '',
        State: selected.States || '',
        Country: selected.Country || '',
      }));
    } else {
      setCreateClientData(prev => ({ ...prev, PostCode: '', PostalId: 0, PostHeadOffice: '', PostPlace: '', District: '', State: '', Country: '' }));
    }
  };

  const handleCcChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreateClientData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (createClientErrors[name]) setCreateClientErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCreateClient = async () => {
    const errs = {};
    if (!createClientData.Name.trim()) errs.Name = 'Name is required';
    if (createClientData.OpeningBalance !== '' && isNaN(createClientData.OpeningBalance)) errs.OpeningBalance = 'Enter a valid amount';
    if (Object.keys(errs).length) { setCreateClientErrors(errs); return; }
    // Duplicate check against existing masters
    const dup = masters.find(m => m.Name?.trim().toLowerCase() === createClientData.Name.trim().toLowerCase() && m.Type === 1);
    if (dup) {
      setCreateClientDupMsg(`A Customer with the name "${createClientData.Name}" already exists.`);
      setCreateClientDupOpen(true);
      return;
    }
    setCreateClientLoading(true);
    try {
      const payload = {
        Name: createClientData.Name.trim(),
        ContactNumber: createClientData.ContactNumber || null,
        Type: 1,
        AddressLine1: createClientData.AddressLine1 || null,
        AddressLine2: createClientData.AddressLine2 || null,
        GSTNumber: createClientData.GSTNumber || null,
        Remarks: createClientData.Remarks || null,
        OpeningBalance: parseFloat(createClientData.OpeningBalance) || 0,
        IsPayable: createClientData.IsPayable,
        PostCode: createClientData.PostCode || null,
        PostalId: parseInt(createClientData.PostalId) || 0,
        PostHeadOffice: createClientData.PostHeadOffice || null,
        PostPlace: createClientData.PostPlace || null,
        District: createClientData.District || null,
        State: createClientData.State || null,
        Country: createClientData.Country || null,
        CompanyId: companyId,
        CreatedBy: userId,
        CreatedDate: new Date(),
      };
      const res = await postData('MasterApi','', payload, token);
      // Reload masters list
      const r = await fetchData('MasterApi', `/${companyId}`, token);
      const updated = (r?.responseCode === '1111' && r.response_data)
        ? (Array.isArray(r.response_data) ? r.response_data : [r.response_data])
        : masters;
      setMasters(updated);
      // Auto-select the newly created client
      const newId = res?.response_data?.Id || res?.Id || null;
      const newClient = newId
        ? updated.find(m => m.Id === newId)
        : updated.find(m => m.Name?.trim().toLowerCase() === payload.Name.toLowerCase() && m.Type === 1);
      if (newClient) {
        setFormData(prev => ({
          ...prev,
          ClientId: newClient.Id,
          ClientName: newClient.Name || '',
          ContactNumber: newClient.ContactNumber || '',
          Address: newClient.AddressLine1 || '',
        }));
      }
      setCreateClientOpen(false);
    } catch (err) {
      console.error('Error creating client:', err);
    } finally {
      setCreateClientLoading(false);
    }
  };

  // Detail row helpers
  const addRow = () => {
    const newRow = { ...EMPTY_DETAIL, _key: Date.now() };
    setDetails(prev => [...prev, newRow]);
    setEditingRow(details.length);
  };

  const removeRow = (idx) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
    if (editingRow === idx) setEditingRow(null);
  };

  const updateDetailField = (idx, field, value) => {
    setDetails(prev => {
      const updated = [...prev];
      let row = { ...updated[idx], [field]: value };

      // Auto-fill rate when a lookup item is selected
      const rateMap = {
        FrameId:      { list: frames,      rateField: 'FrameRate',
                        extra: (item, r) => { r.Thicknes = item?.Thickness || 0; } },
        GlassId:      { list: glasses,     rateField: 'GlassRate' },
        MountId:      { list: mounts,      rateField: 'MountRate' },
        BoardId:      { list: boards,      rateField: 'BoardRate' },
        LaminationId: { list: laminations, rateField: 'LaminationRate' },
        TapeId:       { list: tapes,       rateField: 'TapeRate' },
        StandId:      { list: stands,      rateField: 'StandRate',
                        extraField: 'StandLabourCharge', extraKey: 'LabourCharge' },
        PrintId:      { list: prints,      rateField: 'PrintRate' },
      };
      if (rateMap[field]) {
        const { list, rateField, extraField, extraKey, extra } = rateMap[field];
        const item = list.find(x => x.Id === parseInt(value));
        row[rateField] = item?.Rate || 0;
        if (extraField) row[extraField] = item?.[extraKey] || 0;
        if (extra) extra(item, row);
      } else {
        // Re-resolve per-unit rates from master lists using existing IDs
        // This ensures rates are correct when editing rows loaded from API
        if (row.FrameId) {
          const fr = frames.find(x => x.Id === parseInt(row.FrameId));
          if (fr) { row.FrameRate = fr.Rate || 0; row.Thicknes = fr.Thickness || row.Thicknes || 0; }
        }
        if (row.GlassId) {
          const gl = glasses.find(x => x.Id === parseInt(row.GlassId));
          if (gl) row.GlassRate = gl.Rate || 0;
        }
        if (row.MountId) {
          const mt = mounts.find(x => x.Id === parseInt(row.MountId));
          if (mt) row.MountRate = mt.Rate || 0;
        }
        if (row.BoardId) {
          const bd = boards.find(x => x.Id === parseInt(row.BoardId));
          if (bd) row.BoardRate = bd.Rate || 0;
        }
        if (row.LaminationId) {
          const lm = laminations.find(x => x.Id === parseInt(row.LaminationId));
          if (lm) row.LaminationRate = lm.Rate || 0;
        }
        if (row.TapeId) {
          const tp = tapes.find(x => x.Id === parseInt(row.TapeId));
          if (tp) row.TapeRate = tp.Rate || 0;
        }
        if (row.StandId) {
          const st = stands.find(x => x.Id === parseInt(row.StandId));
          if (st) { row.StandRate = st.Rate || 0; row.StandLabourCharge = st.LabourCharge || 0; }
        }
        if (row.PrintId) {
          const pr = prints.find(x => x.Id === parseInt(row.PrintId));
          if (pr) row.PrintRate = pr.Rate || 0;
        }
      }

      // When user types DiscAmt directly, clear Discper so amt-mode is used
      if (field === 'DiscAmt') {
        row.Discper = 0;
      }
      // When user types Discper directly, clear DiscAmt so percent-mode is used
      if (field === 'Discper') {
        row.DiscAmt = 0;
      }
      // Additional disc mode-switching
      if (field === 'AdditionalDiscAmt') {
        row.AdditionalDiscPer = 0;
      }
      if (field === 'AdditionalDiscPer') {
        row.AdditionalDiscAmt = 0;
      }
      // Recalculate using the engine
      const { perimeter, area, Rate, Amount, DiscAmt, Discper, AdditionalDiscAmt, AdditionalDiscPer, SizeId, HookId, Length } = calcRowAmount(row, sizes, prints, hooks, companySettings);
      row.Perimeter          = perimeter;
      row.Area               = area;
      row.Rate               = Rate;
      row.Amount             = Amount;
      row.DiscAmt            = DiscAmt;
      row.Discper            = Discper;
      row.AdditionalDiscAmt  = AdditionalDiscAmt;
      row.AdditionalDiscPer  = AdditionalDiscPer;
      row.SizeId             = SizeId;
      row.HookId             = HookId;
      row.Length             = Length;

      updated[idx] = row;
      return updated;
    });
  };

  const validate = () => {
    const e = {};
    if (!formData.ClientName?.trim() && !formData.ClientId) e.ClientName = 'Client is required';
    if (!formData.StatusId) e.StatusId = 'Status is required';
    if (!formData.Location) e.Location = 'Location is required';

    const total = parseFloat(formData.TotalAmount) || 0;
    const advance = parseFloat(formData.AdvanceAmount) || 0;
    const paid = parseFloat(formData.PaidAmount) || 0;
    const card = parseFloat(formData.CardAmount) || 0;
    const upi = parseFloat(formData.UpiAmount) || 0;
    const balance = parseFloat(formData.BalanceAmount) || 0;
    const paymentSum = parseFloat((advance + paid + card + upi + balance).toFixed(2));
    
    if (balance < 0) {
      e.payment = `Payments exceed Total Amount. Total payments: ${(advance + paid + card + upi).toFixed(2)}, Total Amount: ${total.toFixed(2)}`;
    } else if (paymentSum !== total) {
      e.payment = `Payment mismatch: Advance + Cash + Card + UPI + Balance (${paymentSum.toFixed(2)}) must equal Total Amount (${total.toFixed(2)})`;
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const dataToSend = {
        Estimate: {
          Id: mode === 'edit' ? (EstimateData?.Id || 0) : 0,
          CompanyId: companyId,
          OrderNumber: parseInt(formData.OrderNumber) || 0,
          ClientId: formData.ClientId || null,
          ClientName: formData.ClientName || '',
          ContactNumber: formData.ContactNumber || '',
          Address: formData.Address || '',
          StatusId: parseInt(formData.StatusId) || 1,
          Location: parseInt(formData.Location) || 0,
          EstimateDate: formData.EstimateDate || today(),
          OrderDate: formData.OrderDate || today(),
          PaymentType: parseInt(formData.PaymentType) || 1,
          PaymentBankId: formData.PaymentBankId || null,
          DiscountRate: formData.DiscountRate || 0,
          AdditionalDiscPer: formData.AdditionalDiscRate || 0,
          Amount: parseFloat(formData.Amount) || 0,
          OtherAmount: parseFloat(formData.OtherAmount) || 0,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          AdvanceAmount: parseFloat(formData.AdvanceAmount) || 0,
          PaidAmount: parseFloat(formData.PaidAmount) || 0,
          CardAmount: parseFloat(formData.CardAmount) || 0,
          UpiAmount: parseFloat(formData.UpiAmount) || 0,
          BalanceAmount: parseFloat(formData.BalanceAmount) || 0,
          Roundoff: Math.round((parseFloat(formData.Roundoff) || 0) * 100) / 100,
          Remarks: formData.Remarks || '',
          EstimateFlag: formData.EstimateFlag || false,
          IsPettyCash: formData.IsPettyCash || false,
          FinYear: formData.FinYear || '',
          IsActive: formData.IsActive ?? true,
          CreatedBy: mode === 'create' ? userId : (EstimateData?.CreatedBy || userId),
          CreatedDate: mode === 'create' ? null : (EstimateData?.CreatedDate || null),
          ModifiedBy: mode === 'edit' ? userId : null,
          ModifiedDate: mode === 'edit' ? new Date().toISOString() : null,
        },
        EstimateDetails: details.map(d => ({
          Id: mode === 'edit' ? (d.Id || 0) : 0,
          CompanyId: companyId,
          EstimateId: mode === 'edit' ? (EstimateData?.Id || 0) : 0,
          SizeId: d.SizeId || null,
          FrameId: d.FrameId || null,
          GlassId: d.GlassId || null,
          MountId: d.MountId || null,
          BoardId: d.BoardId || null,
          LaminationId: d.LaminationId || null,
          TapeId: d.TapeId || null,
          HookId: d.HookId || null,
          HookCount: parseInt(d.HookCount) || 0,
          StandId: d.StandId || null,
          PrintId: d.PrintId || null,
          Width: parseFloat(d.Width) || 0,
          Height: parseFloat(d.Height) || 0,
          MountWidth: parseFloat(d.MountWidth) || 0,
          Thicknes: parseFloat(d.Thicknes) || 0,
          Length: parseFloat(d.Length) || 0,
          Perimeter: parseFloat(d.Perimeter) || 0,
          Area: parseFloat(d.Area) || 0,
          Quantity: parseInt(d.Quantity) || 1,
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.Amount) || 0,
          DiscAmt: parseFloat(d.DiscAmt) || 0,
          Discper: parseFloat(d.Discper) || 0,
          AdditionalDiscAmt: parseFloat(d.AdditionalDiscAmt) || 0,
          AdditionalDiscPer: parseFloat(d.AdditionalDiscPer) || 0,
          FrameRate: parseFloat(d.FrameRate) || 0,
          GlassRate: parseFloat(d.GlassRate) || 0,
          MountRate: parseFloat(d.MountRate) || 0,
          BoardRate: parseFloat(d.BoardRate) || 0,
          LaminationRate: parseFloat(d.LaminationRate) || 0,
          TapeRate: parseFloat(d.TapeRate) || 0,
          StandRate: parseFloat(d.StandRate) || 0,
          StandLabourCharge: parseFloat(d.StandLabourCharge) || 0,
          PrintRate: parseFloat(d.PrintRate) || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: mode === 'create' ? userId : (d.CreatedBy || userId),
          CreatedDate: mode === 'create' ? null : (d.CreatedDate || null),
          ModifiedBy: mode === 'edit' ? userId : null,
          ModifiedDate: mode === 'edit' ? new Date().toISOString() : null,
        })),
      };
      if (mode === 'edit') {
        await updateData('EstimateApi', EstimateData.Id, dataToSend, token);
      } else {
        await postData('EstimateApi', '', dataToSend, token);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving Estimate:', error);
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = mode === 'view';
  const getTitle = () => ({ create: 'New Estimate', edit: 'Edit Estimate', view: 'Estimate Details' }[mode] || 'Estimate');

  // Enrich detail rows with resolved names for PDF
  const enrichDetailsForPDF = (rawDetails) => rawDetails.map(d => ({
    ...d,
    Frame:      { Name: frames.find(x => x.Id === d.FrameId)?.Name || '' },
    Glass:      { Name: glasses.find(x => x.Id === d.GlassId)?.Name || '' },
    Lamination: { Name: laminations.find(x => x.Id === d.LaminationId)?.Name || '' },
    Mount:      { Name: mounts.find(x => x.Id === d.MountId)?.Name || '' },
    Board:      { Name: boards.find(x => x.Id === d.BoardId)?.Name || '' },
    Stand:      { Name: stands.find(x => x.Id === d.StandId)?.Name || '' },
    Tape:       { Name: tapes.find(x => x.Id === d.TapeId)?.Name || '' },
    Print:      { PrintType: prints.find(x => x.Id === d.PrintId)?.PrintType || prints.find(x => x.Id === d.PrintId)?.Name || '' },
  }));

  const handlePrintPDF = () => {
    const pdfData = { ...formData, EstimateDetails: enrichDetailsForPDF(details) };
    printEstimatePDF(pdfData, companyData, companyId);
  };

  const handleWhatsApp = () => {
    const pdfData = { ...formData, EstimateDetails: enrichDetailsForPDF(details) };
    shareEstimateWhatsApp(pdfData, companyData, companyId);
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const inp = { borderRadius: '4px', fontSize: '12px', height: '30px' };
  const lbl = { fontSize: '11px' };
  const hlp = { fontSize: '9px', margin: '1px 0 0 0' };
  const readBg = { backgroundColor: '#f5f5f5' };
  const amtStyle = { ...inp, backgroundColor: '#f0f7f0', fontWeight: '600', textAlign: 'right' };
  const footerInp = { borderRadius: '4px', fontSize: '12px', textAlign: 'right' };
  const footerAmt = { ...footerInp, backgroundColor: '#f0f7f0', fontWeight: '600' };
  const footerLbl = { fontSize: '11px' };

  const selectedClient = masters.find(m => m.Id === formData.ClientId) || null;

  const lookupName = (arr, id) => arr.find(x => x.Id === id)?.Name || '—';

  // Detail columns config removed (unused)

  return (
    <>
    <Dialog
      open={open} onClose={onClose}
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{
        style: {
          width: isMobile ? '100%' : '99vw',
          maxWidth: '99vw',
          borderRadius: isMobile ? '0' : '12px',
          background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
          padding: '2px',
          margin: isMobile ? '0' : '4px',
        }
      }}
    >
      <Box style={{ background: 'white', borderRadius: isMobile ? '0' : '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: isMobile ? '100vh' : 'auto', maxHeight: isMobile ? '100vh' : '95vh' }}>

        {/* ── Title Bar ── */}
        <DialogTitle style={{ background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)', color: 'white', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 'auto', flexShrink: 0 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ReceiptLongIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>{getTitle()}</Typography>
            {formData.OrderNumber ? <Typography style={{ fontSize: '12px', opacity: 0.8 }}>{companyData.BillPrefix || '#'}{formData.OrderNumber}</Typography> : null}
          </Box>
          <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        {/* ── Body ── */}
        <DialogContent style={{ padding: '10px 12px', flex: 1, overflow: 'auto' }}>

          {/* ══ HEADER SECTION ══ */}
          <Grid container spacing={1}>

            {/* Row 1: Client | Estimate Date | Due Date | Order# | Status */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                disabled={isReadOnly}
                options={masters.filter(m => m.Type === 1)}
                value={selectedClient}
                onChange={handleClientSelect}
                getOptionLabel={(o) => o.__createNew ? '' : (o.Name || '')}
                isOptionEqualToValue={(o, v) => o.Id === v?.Id}
                filterOptions={(options, params) => {
                  const input = params.inputValue.trim().toLowerCase();
                  const filtered = options.filter(o =>
                    o.Name?.toLowerCase().includes(input) ||
                    o.ContactNumber?.includes(input)
                  );
                  // Always append a "+ Create Client" option
                  filtered.push({ __createNew: true, __inputValue: params.inputValue.trim(), Name: `+ Create Client${params.inputValue.trim() ? ` "${params.inputValue.trim()}"` : ''}` });
                  return filtered;
                }}
                renderOption={(props, o) => {
                  if (o.__createNew) {
                    return (
                      <li {...props} key="__create_new" style={{ fontSize: '12px', padding: '6px 10px', color: '#2e7d32', fontWeight: '700', borderTop: '1px solid #e0e0e0', background: '#f9fff9' }}>
                        <AddIcon style={{ fontSize: '14px', marginRight: '4px', verticalAlign: 'middle' }} />
                        {o.Name}
                      </li>
                    );
                  }
                  return (
                    <li {...props} key={o.Id} style={{ fontSize: '12px', padding: '4px 10px' }}>
                      <span style={{ fontWeight: '600' }}>{o.Name}</span>
                      {o.ContactNumber && <span style={{ color: '#888', marginLeft: '6px', fontSize: '11px' }}>{o.ContactNumber}</span>}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Client *" size="small" placeholder="Select or type..."
                    error={!!errors.ClientName}
                    helperText={errors.ClientName}
                    InputProps={{ ...params.InputProps, style: inp }}
                    InputLabelProps={{ style: lbl }}
                    FormHelperTextProps={{ style: hlp }}
                  />
                )}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px', fontSize: '12px', height: '30px', padding: '0 8px !important' }, '& .MuiAutocomplete-endAdornment': { top: '4px' } }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField name="EstimateDate" label="Estimate Date" type="date" value={formData.EstimateDate}
                onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl, shrink: true }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField name="OrderDate" label="Due Date" type="date" value={formData.OrderDate}
                onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl, shrink: true }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField name="OrderNumber" label="Order Number *" value={formData.OrderNumber}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                InputProps={{ style: isReadOnly ? { ...inp, ...readBg } : inp }}
                InputLabelProps={{ style: lbl }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={3}>
              <TextField select name="StatusId" label="Status *" value={formData.StatusId}
                onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                error={!!errors.StatusId} helperText={errors.StatusId}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl }}
                FormHelperTextProps={{ style: hlp }}
              >
                {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value} style={{ fontSize: '12px' }}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Row 2: Address | Remarks | Contact | Location | Rate btn | Order chk | Payment */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField name="Address" label="Address" value={formData.Address}
                onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                multiline minRows={2} inputProps={{ maxLength: 250 }}
                InputLabelProps={{ style: lbl }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField name="Remarks" label="Remarks" value={formData.Remarks}
                onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                multiline minRows={2} inputProps={{ maxLength: 250 }}
                InputLabelProps={{ style: lbl }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <TextField name="ContactNumber" label="Contact Number" value={formData.ContactNumber}
                    onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                    inputProps={{ maxLength: 15 }}
                    InputProps={{ style: inp }} InputLabelProps={{ style: lbl }}
                    FormHelperTextProps={{ style: hlp }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select name="Location" label="Location *" value={formData.Location}
                    onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                    error={!!errors.Location} helperText={errors.Location}
                    InputProps={{ style: inp }} InputLabelProps={{ style: lbl }}
                    FormHelperTextProps={{ style: hlp }}
                  >
                    <MenuItem value={1}>Edappally</MenuItem>
                    <MenuItem value={2}>Palarivattom</MenuItem>
                    <MenuItem value={3}>Padivattom</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap' }}>
                    <Box style={{ width: '120px', flexShrink: 0 }}>
                      <TextField name="DiscountRate" label="Discount %" value={formData.DiscountRate}
                        onChange={(e) => {
                          const discVal = parseFloat(e.target.value) || 0;
                          setFormData(prev => ({ ...prev, DiscountRate: parseFloat(e.target.value) || 0 }));
                          // Apply discount to all detail rows
                          if (details.length > 0) {
                            setDetails(prev => prev.map(row => {
                              const updatedRow = { ...row, Discper: discVal, DiscAmt: 0 };
                              // Re-resolve per-unit rates from master lists
                              if (updatedRow.FrameId) { const fr = frames.find(x => x.Id === parseInt(updatedRow.FrameId)); if (fr) { updatedRow.FrameRate = fr.Rate || 0; updatedRow.Thicknes = fr.Thickness || updatedRow.Thicknes || 0; } }
                              if (updatedRow.GlassId) { const gl = glasses.find(x => x.Id === parseInt(updatedRow.GlassId)); if (gl) updatedRow.GlassRate = gl.Rate || 0; }
                              if (updatedRow.MountId) { const mt = mounts.find(x => x.Id === parseInt(updatedRow.MountId)); if (mt) updatedRow.MountRate = mt.Rate || 0; }
                              if (updatedRow.BoardId) { const bd = boards.find(x => x.Id === parseInt(updatedRow.BoardId)); if (bd) updatedRow.BoardRate = bd.Rate || 0; }
                              if (updatedRow.LaminationId) { const lm = laminations.find(x => x.Id === parseInt(updatedRow.LaminationId)); if (lm) updatedRow.LaminationRate = lm.Rate || 0; }
                              if (updatedRow.TapeId) { const tp = tapes.find(x => x.Id === parseInt(updatedRow.TapeId)); if (tp) updatedRow.TapeRate = tp.Rate || 0; }
                              if (updatedRow.StandId) { const st = stands.find(x => x.Id === parseInt(updatedRow.StandId)); if (st) { updatedRow.StandRate = st.Rate || 0; updatedRow.StandLabourCharge = st.LabourCharge || 0; } }
                              if (updatedRow.PrintId) { const pr = prints.find(x => x.Id === parseInt(updatedRow.PrintId)); if (pr) updatedRow.PrintRate = pr.Rate || 0; }
                              const { perimeter, area, Rate, Amount, DiscAmt, SizeId, HookId, Length } = calcRowAmount(updatedRow, sizes, prints, hooks, companySettings);
                              return { ...updatedRow, Perimeter: perimeter, Area: area, Rate, Amount, DiscAmt, SizeId, HookId, Length };
                            }));
                          }
                        }}
                        fullWidth size="small" type="number" disabled={isReadOnly}
                        inputProps={{ step: '0.01', min: 0, max: 100 }}
                        InputProps={{ style: inp }} InputLabelProps={{ style: lbl }}
                        FormHelperTextProps={{ style: hlp }}
                      />
                    </Box>
                    <Box style={{ width: '120px', flexShrink: 0 }}>
                      <TextField name="AdditionalDiscRate" label="Additional Disc %" value={formData.AdditionalDiscRate ?? 0}
                        onChange={(e) => {
                          const addDiscVal = parseFloat(e.target.value) || 0;
                          setFormData(prev => ({ ...prev, AdditionalDiscRate: parseFloat(e.target.value) || 0 }));
                          // Apply additional discount % to all detail rows
                          if (details.length > 0) {
                            setDetails(prev => prev.map(row => {
                              const updatedRow = { ...row, AdditionalDiscPer: addDiscVal, AdditionalDiscAmt: 0 };
                              if (updatedRow.FrameId) { const fr = frames.find(x => x.Id === parseInt(updatedRow.FrameId)); if (fr) { updatedRow.FrameRate = fr.Rate || 0; updatedRow.Thicknes = fr.Thickness || updatedRow.Thicknes || 0; } }
                              if (updatedRow.GlassId) { const gl = glasses.find(x => x.Id === parseInt(updatedRow.GlassId)); if (gl) updatedRow.GlassRate = gl.Rate || 0; }
                              if (updatedRow.MountId) { const mt = mounts.find(x => x.Id === parseInt(updatedRow.MountId)); if (mt) updatedRow.MountRate = mt.Rate || 0; }
                              if (updatedRow.BoardId) { const bd = boards.find(x => x.Id === parseInt(updatedRow.BoardId)); if (bd) updatedRow.BoardRate = bd.Rate || 0; }
                              if (updatedRow.LaminationId) { const lm = laminations.find(x => x.Id === parseInt(updatedRow.LaminationId)); if (lm) updatedRow.LaminationRate = lm.Rate || 0; }
                              if (updatedRow.TapeId) { const tp = tapes.find(x => x.Id === parseInt(updatedRow.TapeId)); if (tp) updatedRow.TapeRate = tp.Rate || 0; }
                              if (updatedRow.StandId) { const st = stands.find(x => x.Id === parseInt(updatedRow.StandId)); if (st) { updatedRow.StandRate = st.Rate || 0; updatedRow.StandLabourCharge = st.LabourCharge || 0; } }
                              if (updatedRow.PrintId) { const pr = prints.find(x => x.Id === parseInt(updatedRow.PrintId)); if (pr) updatedRow.PrintRate = pr.Rate || 0; }
                              const { perimeter, area, Rate, Amount, DiscAmt, Discper, AdditionalDiscAmt, AdditionalDiscPer, SizeId, HookId, Length } = calcRowAmount(updatedRow, sizes, prints, hooks, companySettings);
                              return { ...updatedRow, Perimeter: perimeter, Area: area, Rate, Amount, DiscAmt, Discper, AdditionalDiscAmt, AdditionalDiscPer, SizeId, HookId, Length };
                            }));
                          }
                        }}
                        fullWidth size="small" type="number" disabled={isReadOnly}
                        inputProps={{ step: '0.01', min: 0, max: 100 }}
                        InputProps={{ style: inp }} InputLabelProps={{ style: lbl }}
                        FormHelperTextProps={{ style: hlp }}
                      />
                    </Box>
                    <FormControlLabel
                      control={<Checkbox name="EstimateFlag" checked={formData.EstimateFlag} onChange={handleChange} disabled={isReadOnly} size="small"
                        sx={{ '&.Mui-checked': { color: '#5c3d1e' }, padding: '2px' }} />}
                      label={<Typography style={{ fontSize: '11px' }}>Order</Typography>}
                      style={{ margin: 0, flexShrink: 0 }}
                    />
                    <FormControlLabel
                      control={<Checkbox name="IsPettyCash" checked={formData.IsPettyCash} onChange={handleChange} disabled={isReadOnly} size="small"
                        sx={{ '&.Mui-checked': { color: '#5c3d1e' }, padding: '2px' }} />}
                      label={<Typography style={{ fontSize: '11px' }}>Is Petty Cash</Typography>}
                      style={{ margin: 0, flexShrink: 0 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

          </Grid>

          {/* ══ DETAIL TABLE ══ */}
          <Box style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
            {/* Table header */}
            <Box style={{ background: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)', display: 'flex', alignItems: 'center', padding: '0 8px', minHeight: '28px' }}>
              <Typography style={{ color: 'white', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>
                Product Details
              </Typography>
              {!isReadOnly && (
                <Box style={{ display: 'flex', gap: '4px' }}>
                  <Button size="small" onClick={addRow}
                    style={{ background: '#38a169', color: 'white', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', textTransform: 'none', minHeight: '22px', border: 'none' }}>
                    <AddIcon style={{ fontSize: '14px', marginRight: '2px' }} /> Product
                  </Button>
                </Box>
              )}
            </Box>

            <Box style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ background: '#2d3748' }}>
                    {['WIDTH', 'HEIGHT', 'MNT-WIDTH', 'SIZE', 'FRAME', 'THICKNES', 'LENGTH', 'GLASS', 'MNT-TYPE', 'BOARD', 'LAMN.', 'TAPE', 'HOOK', 'STAND', 'PRINT', 'QTY', 'RATE', 'DISC%', 'DISC AMT', 'ADD DISC%', 'ADD DISC AMT', 'AMOUNT', ''].map(h => (
                      <th key={h} style={{ padding: '4px 6px', color: 'white', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', textAlign: ['QTY', 'RATE', 'DISC%', 'DISC AMT', 'ADD DISC%', 'ADD DISC AMT', 'AMOUNT'].includes(h) ? 'right' : 'center' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {details.length === 0 ? (
                    <tr>
                      <td colSpan={isReadOnly ? 21 : 21} style={{ textAlign: 'center', padding: '16px', color: '#999', fontSize: '12px' }}>
                        No products added. Click "+ Product" to add.
                      </td>
                    </tr>
                  ) : details.map((row, idx) => (
                    <DetailRow
                      key={row._key || row.Id || idx}
                      row={row} idx={idx}
                      isReadOnly={isReadOnly}
                      isEditing={editingRow === idx}
                      onEdit={() => setEditingRow(idx)}
                      onRemove={() => removeRow(idx)}
                      onChange={updateDetailField}
                      sizes={sizes} frames={frames} glasses={glasses} mounts={mounts}
                      boards={boards} laminations={laminations} tapes={tapes}
                      hooks={hooks} stands={stands} prints={prints}
                      lookupName={lookupName}
                    />
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>

          {/* ══ FOOTER TOTALS ══ */}
          <Box style={{ display: 'flex', gap: '6px', marginTop: '8px', paddingTop: '10px', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField label="Amount" value={formData.Amount.toFixed(2)} fullWidth size="small" disabled
                InputProps={{ style: { ...footerAmt } }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="OtherAmount" label="Other Amount" value={formData.OtherAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField label="Roundoff" value={formData.Roundoff.toFixed(2)} fullWidth size="small" disabled
                InputProps={{ style: { ...footerAmt } }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField label="Total Amount" value={formData.TotalAmount.toFixed(2)} fullWidth size="small" disabled
                InputProps={{ style: { ...footerAmt, backgroundColor: '#e8f5e9' } }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            {/*<Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="AdvanceAmount" label="Advance Amount" value={formData.AdvanceAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>*/}
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="PaidAmount" label="Cash Amount" value={formData.PaidAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="CardAmount" label="Card Amount" value={formData.CardAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="UpiAmount" label="UPI Amount" value={formData.UpiAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            {(parseFloat(formData.CardAmount) > 0 || parseFloat(formData.UpiAmount) > 0) && (
              <Box style={{ flex: 1, minWidth: '120px' }}>
                <TextField
                  select
                  name="PaymentBankId"
                  label="Bank"
                  value={formData.PaymentBankId ?? ''}
                  onChange={e => setFormData(prev => ({ ...prev, PaymentBankId: e.target.value ? parseInt(e.target.value) : null }))}
                  fullWidth size="small" disabled={isReadOnly}
                  InputProps={{ style: { ...footerInp, backgroundColor: '#e3f2fd', borderColor: '#1976d2' } }}
                  InputLabelProps={{ style: { ...footerLbl, color: '#1565c0' }, shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1976d2' }, '&:hover fieldset': { borderColor: '#1565c0' }, '&.Mui-focused fieldset': { borderColor: '#0d47a1' } } }}
                >
                  <MenuItem value=""><em>— Select Bank —</em></MenuItem>
                  {banks.map(b => (
                    <MenuItem key={b.Id} value={b.Id}>{b.Name}</MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField label="Balance Amount" value={formData.BalanceAmount.toFixed(2)} fullWidth size="small" disabled
                InputProps={{ style: { ...footerAmt, backgroundColor: formData.BalanceAmount < 0 ? '#ffebee' : formData.BalanceAmount > 0 ? '#fff8e1' : '#e8f5e9', color: formData.BalanceAmount < 0 ? '#d32f2f' : 'inherit' } }}
                InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
          </Box>
          {errors.payment && (
            <Typography style={{ color: '#d32f2f', fontSize: '11px', marginTop: '4px' }}>
              ⚠ {errors.payment}
            </Typography>
          )}

        </DialogContent>

        {/* ── Actions ── */}
        <DialogActions style={{ padding: '8px 12px', background: '#f8f9fa', borderTop: '1px solid #e9ecef', gap: '6px', minHeight: 'auto', flexShrink: 0 }}>
          <Button onClick={onClose} style={{ color: '#6c757d', textTransform: 'none', fontWeight: '500', fontSize: '12px', padding: '4px 8px' }}>
            Cancel
          </Button>
          {(mode === 'view' || mode === 'edit') && (
            <Button onClick={handlePrintPDF} variant="outlined"
              style={{ borderColor: '#e65100', color: '#e65100', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 16px', fontSize: '12px' }}>
              🖨️ Print PDF
            </Button>
          )}
          {(mode === 'view' || mode === 'edit') && (
            <Button onClick={handleWhatsApp} variant="outlined"
              style={{ borderColor: '#25D366', color: '#25D366', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 16px', fontSize: '12px' }}>
              💬 WhatsApp
            </Button>
          )}
          {(mode === 'view' || mode === 'edit') && onConvertToBill && (
            <Button
              onClick={() => onConvertToBill({ header: formData, details })}
              disabled={!!formData.IsConverted}
              variant="contained"
              style={{ background: formData.IsConverted ? '#b0bec5' : 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 16px', fontSize: '12px' }}>
              🧾 {formData.IsConverted ? 'Already Converted' : 'Convert to Bill'}
            </Button>
          )}
          {!isReadOnly && (
            <Button onClick={handleSave} disabled={loading} variant="contained"
              style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #9a7a2e 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 16px', fontSize: '12px' }}>
              {loading ? <CircularProgress size={14} style={{ color: 'white', marginRight: '6px' }} /> : null}
              {loading ? 'Saving...' : mode === 'create' ? 'Save Estimate' : 'Update Estimate'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>

    {/* ── Quick Create Client Dialog (full MasterModal equivalent) ── */}
    <Dialog open={createClientOpen} onClose={() => setCreateClientOpen(false)} maxWidth="md" fullWidth
      PaperProps={{ style: { borderRadius: isMobile ? '0' : '12px', background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)', padding: '2px', margin: isMobile ? '0' : '8px' } }}>
      <Box style={{ background: 'white', borderRadius: isMobile ? '0' : '10px', overflow: 'hidden' }}>
        <DialogTitle style={{ background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)', color: 'white', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 'auto' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AddIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>New Client</Typography>
          </Box>
          <IconButton onClick={() => setCreateClientOpen(false)} style={{ color: 'white', padding: '2px' }} size="small">
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: '8px 12px', paddingBottom: '4px' }}>
          <Typography style={{ color: '#666', marginBottom: '6px', fontSize: '10px' }}>Add a new customer to your system</Typography>
          <Grid container spacing={1}>

            {/* ── Basic Info ── */}
            <Grid item xs={12}>
              <Typography style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px' }}>Basic Information</Typography>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField name="Name" label="Name *" value={createClientData.Name} onChange={handleCcChange}
                fullWidth size="small" autoFocus error={!!createClientErrors.Name} helperText={createClientErrors.Name}
                inputProps={{ maxLength: 200 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px' } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
                FormHelperTextProps={{ style: { fontSize: '9px', margin: '1px 0 0' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="ContactNumber" label="Contact Number" value={createClientData.ContactNumber} onChange={handleCcChange}
                fullWidth size="small" inputRef={contactNumberRef} inputProps={{ maxLength: 15 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px' } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
                FormHelperTextProps={{ style: { fontSize: '9px', margin: '1px 0 0' } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select name="Type" label="Type *" value={1} fullWidth size="small" disabled
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px', padding: 0 } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}>
                <MenuItem value={1} style={{ fontSize: '13px' }}>Customer</MenuItem>
              </TextField>
            </Grid>

            {/* ── Address ── */}
            <Grid item xs={12}>
              <Typography style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px', marginTop: '4px' }}>Address</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="AddressLine1" label="Address Line 1" value={createClientData.AddressLine1} onChange={handleCcChange}
                fullWidth size="small" placeholder="Street / Area" inputProps={{ maxLength: 150 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px' } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
                FormHelperTextProps={{ style: { fontSize: '9px', margin: '1px 0 0' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="AddressLine2" label="Address Line 2" value={createClientData.AddressLine2} onChange={handleCcChange}
                fullWidth size="small" placeholder="City / State / PIN" inputProps={{ maxLength: 150 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px' } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
                FormHelperTextProps={{ style: { fontSize: '9px', margin: '1px 0 0' } }}
              />
            </Grid>

            {/* ── Postal Details ── */}
            <Grid item xs={12}>
              <Typography style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px', marginTop: '4px' }}>Postal Details</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={ccPostalOptions}
                value={ccSelectedPostal}
                inputValue={ccPostalInput}
                onInputChange={handleCcPostalInputChange}
                onChange={handleCcPostalSelect}
                getOptionLabel={(opt) => opt ? `${opt.PinCode || ''}${opt.PostPlace ? ' – ' + opt.PostPlace : ''}${opt.District ? ' – ' + opt.District : ''}` : ''}
                isOptionEqualToValue={(opt, val) => opt.Id === val?.Id}
                loading={ccPostalLoading}
                filterOptions={(x) => x}
                noOptionsText={ccPostalLoading ? 'Loading...' : 'No results'}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.Id} style={{ fontSize: '12px', padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: '700', color: '#5c3d1e', minWidth: '52px', fontSize: '12px' }}>{opt.PinCode}</span>
                      <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '12px' }}>{opt.PostPlace}</span>
                      {opt.District && <span style={{ color: '#888', fontSize: '11px' }}>{opt.District}</span>}
                    </div>
                    {(opt.States || opt.PostHeadOffice) && (
                      <div style={{ fontSize: '10px', color: '#aaa', marginTop: '1px', paddingLeft: '60px' }}>
                        {[opt.PostHeadOffice, opt.States].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Post Code" size="small" placeholder="Type to search..."
                    InputProps={{ ...params.InputProps, style: { borderRadius: '6px', fontSize: '13px', height: '32px' },
                      endAdornment: (<>{ccPostalLoading && <CircularProgress size={12} style={{ color: '#5c3d1e' }} />}{params.InputProps.endAdornment}</>)
                    }}
                    InputLabelProps={{ style: { fontSize: '11px' } }}
                  />
                )}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '13px', height: '32px', padding: '0 8px !important' }, '& .MuiAutocomplete-endAdornment': { top: '4px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="PostHeadOffice" label="Head Office" value={createClientData.PostHeadOffice} onChange={handleCcChange}
                fullWidth size="small" placeholder="Auto-filled on selection" inputProps={{ maxLength: 50 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px', backgroundColor: ccSelectedPostal ? '#f0f7f0' : undefined } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="PostPlace" label="Post Place" value={createClientData.PostPlace} onChange={handleCcChange}
                fullWidth size="small" placeholder="Auto-filled on selection" inputProps={{ maxLength: 50 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px', backgroundColor: ccSelectedPostal ? '#f0f7f0' : undefined } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="District" label="District" value={createClientData.District} onChange={handleCcChange}
                fullWidth size="small" placeholder="Auto-filled on selection" inputProps={{ maxLength: 50 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px', backgroundColor: ccSelectedPostal ? '#f0f7f0' : undefined } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="State" label="State" value={createClientData.State} onChange={handleCcChange}
                fullWidth size="small" placeholder="Auto-filled on selection" inputProps={{ maxLength: 50 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px', backgroundColor: ccSelectedPostal ? '#f0f7f0' : undefined } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="Country" label="Country" value={createClientData.Country} onChange={handleCcChange}
                fullWidth size="small" placeholder="Auto-filled on selection" inputProps={{ maxLength: 50 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px', backgroundColor: ccSelectedPostal ? '#f0f7f0' : undefined } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
              />
            </Grid>

            {/* ── Financial Details ── */}
            <Grid item xs={12}>
              <Typography style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px', marginTop: '4px' }}>Financial Details</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="GSTNumber" label="GST Number" value={createClientData.GSTNumber} onChange={handleCcChange}
                fullWidth size="small" placeholder="Enter GST number" inputProps={{ maxLength: 15 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px' } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="OpeningBalance" label="Opening Balance (₹)" value={createClientData.OpeningBalance} onChange={handleCcChange}
                fullWidth size="small" type="number" placeholder="0.00"
                error={!!createClientErrors.OpeningBalance} helperText={createClientErrors.OpeningBalance}
                inputProps={{ step: '0.01' }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px', height: '32px' } }}
                InputLabelProps={{ style: { fontSize: '11px' } }}
                FormHelperTextProps={{ style: { fontSize: '9px', margin: '1px 0 0' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch name="IsPayable" checked={createClientData.IsPayable} onChange={handleCcChange} size="small"
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }}
                  />
                }
                label={<Typography style={{ fontSize: '12px', color: '#2d3748' }}>Is Payable</Typography>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField name="Remarks" label="Remarks" value={createClientData.Remarks} onChange={handleCcChange}
                fullWidth size="small" placeholder="Any additional notes"
                inputProps={{ maxLength: 200 }} multiline minRows={2}
                InputLabelProps={{ style: { fontSize: '11px' } }}
                FormHelperTextProps={{ style: { fontSize: '9px', margin: '1px 0 0' } }}
              />
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions style={{ padding: '8px 12px', background: '#f8f9fa', borderTop: '1px solid #e9ecef', gap: '6px', minHeight: 'auto' }}>
          <Button onClick={() => setCreateClientOpen(false)} style={{ color: '#6c757d', textTransform: 'none', fontWeight: '500', fontSize: '12px', padding: '4px 8px' }}>
            Cancel
          </Button>
          <Button onClick={handleCreateClient} disabled={createClientLoading} variant="contained"
            style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #9a7a2e 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 16px', fontSize: '12px' }}>
            {createClientLoading ? <CircularProgress size={13} style={{ color: 'white', marginRight: '6px' }} /> : null}
            {createClientLoading ? 'Saving...' : 'Add Client'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>

    {/* ── Duplicate Client Warning ── */}
    <Dialog open={createClientDupOpen} onClose={() => setCreateClientDupOpen(false)} maxWidth="xs" fullWidth
      PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
      <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
        <Typography style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</Typography>
        <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '12px', color: '#2d3748', fontSize: '16px' }}>Duplicate Client Found</Typography>
        <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>{createClientDupMsg}</Typography>
      </DialogContent>
      <DialogActions style={{ padding: '0 24px 24px', justifyContent: 'center' }}>
        <Button onClick={() => setCreateClientDupOpen(false)} variant="contained"
          style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #7a5230 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '8px 32px', fontSize: '14px' }}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

// ─── Detail Row Sub-component ─────────────────────────────────────────────────
const noSpinStyle = document.createElement('style');
noSpinStyle.textContent = '.est-no-spin::-webkit-outer-spin-button, .est-no-spin::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; } .est-no-spin { -moz-appearance: textfield; }';
if (!document.head.querySelector('style[data-est-no-spin]')) {
  noSpinStyle.setAttribute('data-est-no-spin', '1');
  document.head.appendChild(noSpinStyle);
}
const DetailRow = ({ row, idx, isReadOnly, isEditing, onEdit, onRemove, onChange, sizes, frames, glasses, mounts, boards, laminations, tapes, hooks, stands, prints, lookupName }) => {
  const cellStyle = { padding: '3px 4px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' };
  const inputCellStyle = { padding: '2px 3px', borderBottom: '1px solid #f0f0f0' };
  const numInp = { fontSize: '11px', height: '26px', padding: '0 4px', borderRadius: '3px', border: '1px solid #ccc', width: '100%', textAlign: 'right', outline: 'none', MozAppearance: 'textfield', WebkitAppearance: 'none' };
  const selInp = { fontSize: '11px', height: '26px', padding: '0 2px', borderRadius: '3px', border: '1px solid #ccc', width: '100%', outline: 'none' };
  const rowBg = idx % 2 === 0 ? '#ffffff' : '#fdf6ec';

  const sel = (field, arr, labelKey = 'Name') => (
    <select value={row[field] || ''} disabled={isReadOnly}
      onChange={e => onChange(idx, field, e.target.value ? parseInt(e.target.value) : null)}
      style={selInp}>
      <option value="">—</option>
      {arr.map(x => <option key={x.Id} value={x.Id}>{x[labelKey] || x.Name}</option>)}
    </select>
  );

  const num = (field, w = '52px') => (
    <input type="number" value={row[field] ?? ''} disabled={isReadOnly}
      onChange={e => onChange(idx, field, e.target.value)}
      className="est-no-spin"
      style={{ ...numInp, width: w }} />
  );

  if (!isEditing && !isReadOnly) {
    // Compact read row with click to edit
    return (
      <tr style={{ background: rowBg, cursor: 'pointer' }} onClick={onEdit}>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Width || '—'}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Height || '—'}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.MountWidth || '—'}</td>
        <td style={cellStyle}>{lookupName(sizes, row.SizeId)}</td>
        <td style={cellStyle}>{lookupName(frames, row.FrameId)}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Thicknes || '—'}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Length || '—'}</td>
        <td style={cellStyle}>{lookupName(glasses, row.GlassId)}</td>
        <td style={cellStyle}>{lookupName(mounts, row.MountId)}</td>
        <td style={cellStyle}>{lookupName(boards, row.BoardId)}</td>
        <td style={cellStyle}>{lookupName(laminations, row.LaminationId)}</td>
        <td style={cellStyle}>{lookupName(tapes, row.TapeId)}</td>
        <td style={cellStyle}>{row.HookCount > 0 ? row.HookCount : '—'}</td>
        <td style={cellStyle}>{lookupName(stands, row.StandId)}</td>
        <td style={cellStyle}>{prints.find(x => x.Id === row.PrintId)?.PrintType || '—'}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Quantity}</td>
        <td style={{ ...cellStyle, textAlign: 'right', color: '#5c3d1e', fontWeight: '600' }}>{parseFloat(row.Rate || 0).toFixed(2)}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{parseFloat(row.Discper || 0).toFixed(2)}%</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{parseFloat(row.DiscAmt || 0).toFixed(2)}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{parseFloat(row.AdditionalDiscPer || 0).toFixed(2)}%</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{parseFloat(row.AdditionalDiscAmt || 0).toFixed(2)}</td>
        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: '700', color: '#2d3748' }}>₹{parseFloat(row.Amount || 0).toFixed(2)}</td>
        <td style={cellStyle} onClick={e => e.stopPropagation()}>
          <Tooltip title="Remove row" arrow>
            <IconButton size="small" onClick={onRemove} style={{ padding: '2px', color: '#e53e3e' }}>
              <DeleteIcon style={{ fontSize: '14px' }} />
            </IconButton>
          </Tooltip>
        </td>
      </tr>
    );
  }

  // Editing row (inline inputs)
  return (
    <tr style={{ background: '#fffbf0', outline: '2px solid #c9a84c' }}>
      <td style={inputCellStyle}>{num('Width', '50px')}</td>
      <td style={inputCellStyle}>{num('Height', '50px')}</td>
      <td style={inputCellStyle}>{num('MountWidth', '50px')}</td>
      <td style={inputCellStyle}>{sel('SizeId', sizes)}</td>
      <td style={inputCellStyle}>{sel('FrameId', frames)}</td>
      <td style={inputCellStyle}>{num('Thicknes', '50px')}</td>
      <td style={inputCellStyle}>{num('Length', '50px')}</td>
      <td style={inputCellStyle}>{sel('GlassId', glasses)}</td>
      <td style={inputCellStyle}>{sel('MountId', mounts)}</td>
      <td style={inputCellStyle}>{sel('BoardId', boards)}</td>
      <td style={inputCellStyle}>{sel('LaminationId', laminations)}</td>
      <td style={inputCellStyle}>{sel('TapeId', tapes)}</td>
      <td style={inputCellStyle}>
        <input type="number" value={row.HookCount ?? 0} disabled={isReadOnly}
          onChange={e => onChange(idx, 'HookCount', e.target.value)}
          style={{ ...numInp, width: '52px' }} placeholder="0" />
      </td>
      <td style={inputCellStyle}>{sel('StandId', stands)}</td>
      <td style={inputCellStyle}>{sel('PrintId', prints, 'PrintType')}</td>
      <td style={inputCellStyle}>{num('Quantity', '44px')}</td>
      <td style={{ ...inputCellStyle, textAlign: 'right', color: '#5c3d1e', fontWeight: '700', minWidth: '65px', fontSize: '11px' }}>
        {parseFloat(row.Rate || 0).toFixed(2)}
      </td>
      <td style={inputCellStyle}>{num('Discper', '48px')}</td>
      <td style={inputCellStyle}>{num('DiscAmt', '60px')}</td>
      <td style={inputCellStyle}>{num('AdditionalDiscPer', '52px')}</td>
      <td style={inputCellStyle}>{num('AdditionalDiscAmt', '64px')}</td>
      <td style={{ ...inputCellStyle, textAlign: 'right', fontWeight: '700', color: '#2d3748', minWidth: '70px' }}>
        ₹{parseFloat(row.Amount || 0).toFixed(2)}
      </td>
      {!isReadOnly && (
        <td style={inputCellStyle}>
          <Tooltip title="Remove row" arrow>
            <IconButton size="small" onClick={onRemove} style={{ padding: '2px', color: '#e53e3e' }}>
              <DeleteIcon style={{ fontSize: '14px' }} />
            </IconButton>
          </Tooltip>
        </td>
      )}
    </tr>
  );
};

export default EstimateModal;
